import { useState, useRef, useCallback, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Upload, X, GripVertical, Loader2, ImageOff } from 'lucide-react';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useUploadSkuPhoto, useDeleteSkuPhoto, useReorderSkuPhotos } from '../../hooks/useWmsSkuPhotos';
import type { SkuPhoto } from '../../types';

const MAX_PHOTOS = 10;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const ACCEPT_STRING = ACCEPTED_TYPES.join(',');

function getPhotoUrl(skuId: string, photo: SkuPhoto): string {
  if (photo.mongoFileId) {
    return `/api/v4/legacy/v2/files/${photo.mongoFileId}`;
  }
  return `/api/v4/wms/skus/${skuId}/photos/${photo.id}/download`;
}

// =============================================================================
// Sortable Photo Card
// =============================================================================

function SortablePhotoCard({
  photo,
  skuId,
  onDelete,
  onClick,
}: {
  photo: SkuPhoto;
  skuId: string;
  onDelete: (photo: SkuPhoto) => void;
  onClick: (photo: SkuPhoto) => void;
}) {
  const [imgError, setImgError] = useState(false);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: photo.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'relative group rounded-lg border bg-card overflow-hidden aspect-square',
        isDragging && 'opacity-50 ring-2 ring-primary',
      )}
    >
      {imgError ? (
        <div className="w-full h-full flex items-center justify-center bg-muted text-muted-foreground">
          <ImageOff className="h-6 w-6" />
        </div>
      ) : (
        <img
          src={getPhotoUrl(skuId, photo)}
          alt={photo.originalFilename || 'SKU photo'}
          className="w-full h-full object-cover cursor-pointer"
          onClick={() => onClick(photo)}
          onError={() => setImgError(true)}
        />
      )}

      {/* Hover overlay */}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors pointer-events-none" />

      {/* Drag handle */}
      <div
        {...attributes}
        {...listeners}
        className="absolute top-1 left-1 p-1 rounded bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
      >
        <GripVertical className="h-3.5 w-3.5" />
      </div>

      {/* Delete button */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(photo);
        }}
        className="absolute top-1 right-1 p-1 rounded bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive"
      >
        <X className="h-3.5 w-3.5" />
      </button>

      {/* Filename */}
      {photo.originalFilename && (
        <div className="absolute bottom-0 left-0 right-0 p-1 bg-black/50 text-white text-[10px] truncate opacity-0 group-hover:opacity-100 transition-opacity">
          {photo.originalFilename}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

interface SkuPhotoManagerProps {
  skuId: string;
  photos: SkuPhoto[];
}

export default function SkuPhotoManager({ skuId, photos }: SkuPhotoManagerProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mountedRef = useRef(true);
  const [isDragOver, setIsDragOver] = useState(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [lightboxPhoto, setLightboxPhoto] = useState<SkuPhoto | null>(null);
  const [deletingPhoto, setDeletingPhoto] = useState<SkuPhoto | null>(null);

  const uploadMutation = useUploadSkuPhoto();
  const deleteMutation = useDeleteSkuPhoto();
  const reorderMutation = useReorderSkuPhotos();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const sortedPhotos = [...photos].sort((a, b) => a.sortOrder - b.sortOrder);
  const canUpload = photos.length + uploadingCount < MAX_PHOTOS;

  // File validation
  const validateFiles = useCallback((files: File[]): File[] => {
    const valid: File[] = [];
    const remaining = MAX_PHOTOS - photos.length - uploadingCount;

    for (const file of files) {
      if (valid.length >= remaining) {
        toast.error(`Can only upload ${remaining} more photo(s)`);
        break;
      }
      if (!ACCEPTED_TYPES.includes(file.type)) {
        toast.error(`"${file.name}" is not a supported image type (JPEG, PNG, WebP)`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`"${file.name}" exceeds 10MB limit`);
        continue;
      }
      valid.push(file);
    }
    return valid;
  }, [photos.length, uploadingCount]);

  // Upload handler
  const handleUpload = useCallback(async (files: File[]) => {
    const validFiles = validateFiles(files);
    if (validFiles.length === 0) return;

    setUploadingCount((c) => c + validFiles.length);

    const results = await Promise.allSettled(
      validFiles.map((file) =>
        uploadMutation.mutateAsync({ skuId, file }),
      ),
    );

    setUploadingCount((c) => c - validFiles.length);

    const succeeded = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.filter((r) => r.status === 'rejected').length;

    if (succeeded > 0) {
      toast.success(`${succeeded} photo${succeeded > 1 ? 's' : ''} uploaded`);
    }
    if (failed > 0) {
      toast.error(`${failed} photo${failed > 1 ? 's' : ''} failed to upload`);
    }
  }, [skuId, uploadMutation, validateFiles]);

  // Drop zone handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    handleUpload(files);
  }, [handleUpload]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    handleUpload(files);
    // Reset input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [handleUpload]);

  // Drag-and-drop reorder — no useCallback: sortedPhotos is new every render
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sortedPhotos.findIndex((p) => p.id === active.id);
    const newIndex = sortedPhotos.findIndex((p) => p.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(sortedPhotos, oldIndex, newIndex);
    const photoIds = reordered.map((p) => p.id);

    reorderMutation.mutate({ skuId, photoIds });
  };

  // Delete handler
  const handleDeleteConfirm = useCallback(async () => {
    if (!deletingPhoto) return;
    await deleteMutation.mutateAsync({ skuId, photoId: deletingPhoto.id });
    if (mountedRef.current) setDeletingPhoto(null);
  }, [deletingPhoto, skuId, deleteMutation]);

  return (
    <div className="space-y-4">
      {/* Upload Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => canUpload && fileInputRef.current?.click()}
        className={cn(
          'border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer',
          isDragOver && canUpload
            ? 'border-primary bg-primary/5'
            : 'border-muted-foreground/25 hover:border-muted-foreground/50',
          !canUpload && 'opacity-50 cursor-not-allowed',
        )}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPT_STRING}
          onChange={handleFileSelect}
          className="hidden"
          disabled={!canUpload}
        />
        {uploadingCount > 0 ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-8 w-8 text-muted-foreground animate-spin" />
            <p className="text-sm text-muted-foreground">
              Uploading {uploadingCount} photo{uploadingCount > 1 ? 's' : ''}...
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Upload className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {canUpload
                ? 'Drag & drop images here, or click to browse'
                : `Maximum ${MAX_PHOTOS} photos reached`}
            </p>
            <p className="text-xs text-muted-foreground/70">
              JPEG, PNG, WebP — max 10MB each — {photos.length}/{MAX_PHOTOS} photos
            </p>
          </div>
        )}
      </div>

      {/* Photo Grid */}
      {sortedPhotos.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sortedPhotos.map((p) => p.id)}
            strategy={rectSortingStrategy}
          >
            <div className="grid grid-cols-4 gap-2">
              {sortedPhotos.map((photo) => (
                <SortablePhotoCard
                  key={photo.id}
                  photo={photo}
                  skuId={skuId}
                  onDelete={setDeletingPhoto}
                  onClick={setLightboxPhoto}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Lightbox */}
      <Dialog open={!!lightboxPhoto} onOpenChange={(open) => !open && setLightboxPhoto(null)}>
        <DialogContent className="max-w-3xl p-2">
          {lightboxPhoto && (
            <img
              src={getPhotoUrl(skuId, lightboxPhoto)}
              alt={lightboxPhoto.originalFilename || 'SKU photo'}
              className="w-full h-auto rounded"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingPhoto} onOpenChange={(open) => !open && setDeletingPhoto(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete photo</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {deletingPhoto?.originalFilename ? `"${deletingPhoto.originalFilename}"` : 'this photo'}? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
